import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import CreateOrganizationModal from './CreateOrganizationModal';
import ManageOrgAdminsModal from './ManageOrgAdminsModal';
import OrganizationComponentSettingsModal from './OrganizationComponentSettingsModal';
import OrganizationDeleteModal from './OrganizationDeleteModal';
import SSODomainModal from './SSODomainModal';
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const OrganizationManagement = ({ showDeleted = false }) => {
  const [activeTab, setActiveTab] = useState(showDeleted ? 'deleted' : 'organizations');
  const [organizations, setOrganizations] = useState([]);
  const [deletedOrganizations, setDeletedOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showOrgDeleteModal, setShowOrgDeleteModal] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [showManageAdminsModal, setShowManageAdminsModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [showComponentSettingsModal, setShowComponentSettingsModal] = useState(false);
  const [selectedOrgForSettings, setSelectedOrgForSettings] = useState(null);
  const [showSSODomainModal, setShowSSODomainModal] = useState(false);
  const [selectedOrgForSSO, setSelectedOrgForSSO] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadData();
    }
  }, [user, activeTab, showDeleted]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const requests = [
        axios.get(`${apiBase}/admin/organizations`, { headers }),
        axios.get(`${apiBase}/admin/users`, { headers })
      ];

      // Load deleted organizations if on that tab or if showDeleted prop is true
      if (activeTab === 'deleted' || showDeleted) {
        requests.push(axios.get(`${apiBase}/admin/organizations/deleted`, { headers }));
      }

      const responses = await Promise.all(requests);
      const [orgsResponse, usersResponse, deletedOrgsResponse] = responses;

      setOrganizations(orgsResponse.data);
      setUsers(usersResponse.data);

      if (deletedOrgsResponse) {
        setDeletedOrganizations(deletedOrgsResponse.data);
      }
    } catch (err) {
      console.error('Error loading organization data:', err);
      setError('Failed to load data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (orgData) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/organizations`, orgData, { headers });
      setShowCreateOrgModal(false);
      await loadData();
    } catch (err) {
      console.error('Failed to create organization:', err);
      setError('Failed to create organization: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleManageOrgAdmins = (organization) => {
    setSelectedOrganization(organization);
    setShowManageAdminsModal(true);
  };

  const handleComponentSettings = (org) => {
    setSelectedOrgForSettings(org);
    setShowComponentSettingsModal(true);
  };

  const handleSSODomains = (org) => {
    setSelectedOrgForSSO(org);
    setShowSSODomainModal(true);
  };

  const handleUpdateOrgAdmins = async () => {
    setShowManageAdminsModal(false);
    setSelectedOrganization(null);
    await loadData();
  };

  const handleDeleteOrganization = async (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    if (!org) {
      setError('Organization not found');
      return;
    }
    setOrgToDelete(org);
    setShowOrgDeleteModal(true);
  };

  const confirmOrganizationDelete = async () => {
    if (!orgToDelete) return;

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/admin/organizations/${orgToDelete.id}`, { headers });
      alert(`Organization "${orgToDelete.name}" has been deleted.`);
      await loadData();
      setError('');
    } catch (err) {
      console.error('Failed to delete organization:', err);
      setError('Failed to delete organization: ' + (err.response?.data?.detail || err.message));
    } finally {
      setShowOrgDeleteModal(false);
      setOrgToDelete(null);
    }
  };

  const cancelOrganizationDelete = () => {
    setShowOrgDeleteModal(false);
    setOrgToDelete(null);
  };

  const handleReinstateOrganization = async (orgId) => {
    if (window.confirm('Are you sure you want to reinstate this organization? This will restore all users and data.')) {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        await axios.post(`${apiBase}/admin/organizations/${orgId}/reinstate`, {}, { headers });
        alert('Organization reinstated successfully!');
        await loadData();
      } catch (err) {
        setError('Failed to reinstate organization: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handlePermanentlyDelete = async (orgId) => {
    if (window.confirm('Are you sure you want to PERMANENTLY delete this organization? This action cannot be undone and will remove all data forever.')) {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        await axios.delete(`${apiBase}/admin/organizations/${orgId}/permanent`, { headers });
        alert('Organization permanently deleted.');
        await loadData();
      } catch (err) {
        setError('Failed to permanently delete organization: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="text-center py-12">
        <div className="text-ui-red-solid text-6xl mb-4">🚫</div>
        <h2 className="text-xl font-semibold mb-2 text-ui-ink">Access Denied</h2>
        <p className="text-ui-muted">This area is restricted to superadmin users only.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ui-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-ui-violet-strong to-ui-indigo-strong rounded-lg p-6 text-ui-on-primary">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-ui-on-primary/20 rounded-2xl flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold">Organization Management</h1>
            <p className="text-ui-on-primary/80">Create and manage organizations</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-ui-red-bg border border-ui-red-line text-ui-red-fg px-4 py-3 rounded">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-ui-red-fg/70 hover:text-ui-red-fg">×</button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="rounded-lg shadow-ui-card p-1 bg-ui-surface">
        <div className="grid grid-cols-2 gap-1">
          {[
            { id: 'organizations', label: 'Organizations', icon: '🏢' },
            { id: 'deleted', label: 'Deleted Orgs', icon: '🗑️' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-ui-primary text-ui-on-primary shadow-sm'
                  : 'text-ui-ink-2 hover:text-ui-ink hover:bg-ui-hover'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'organizations' && (
        <OrganizationsTab
          organizations={organizations}
          onCreateOrganization={() => setShowCreateOrgModal(true)}
          onDeleteOrganization={handleDeleteOrganization}
          onManageAdmins={handleManageOrgAdmins}
          onComponentSettings={handleComponentSettings}
          onSSODomains={handleSSODomains}
        />
      )}

      {activeTab === 'deleted' && (
        <DeletedOrganizationsTab
          deletedOrganizations={deletedOrganizations}
          onReinstateOrganization={handleReinstateOrganization}
          onPermanentlyDelete={handlePermanentlyDelete}
          onRefresh={loadData}
        />
      )}

      {/* Modals */}
      {showCreateOrgModal && (
        <CreateOrganizationModal
          users={users}
          onClose={() => setShowCreateOrgModal(false)}
          onCreate={handleCreateOrganization}
        />
      )}

      {showOrgDeleteModal && orgToDelete && (
        <OrganizationDeleteModal
          organization={orgToDelete}
          onConfirm={confirmOrganizationDelete}
          onCancel={cancelOrganizationDelete}
        />
      )}

      {showManageAdminsModal && selectedOrganization && (
        <ManageOrgAdminsModal
          organization={selectedOrganization}
          onClose={() => {
            setShowManageAdminsModal(false);
            setSelectedOrganization(null);
          }}
          onUpdate={handleUpdateOrgAdmins}
        />
      )}

      {showComponentSettingsModal && selectedOrgForSettings && (
        <OrganizationComponentSettingsModal
          organization={selectedOrgForSettings}
          onClose={() => {
            setShowComponentSettingsModal(false);
            setSelectedOrgForSettings(null);
          }}
          onUpdate={() => {
            loadData();
          }}
        />
      )}

      {showSSODomainModal && selectedOrgForSSO && (
        <SSODomainModal
          organization={selectedOrgForSSO}
          currentUser={user}
          onClose={() => {
            setShowSSODomainModal(false);
            setSelectedOrgForSSO(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

// Organizations Tab Component
const OrganizationsTab = ({ organizations, onCreateOrganization, onDeleteOrganization, onManageAdmins, onComponentSettings, onSSODomains }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingEmailOrgId, setEditingEmailOrgId] = useState(null);
  const [editEmailPrefix, setEditEmailPrefix] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const itemsPerPage = 20;

  const handleEditEmail = (org) => {
    setEditingEmailOrgId(org.id);
    setEditEmailPrefix(org.email_slug || '');
  };

  const handleSaveEmail = async (orgId) => {
    const prefix = editEmailPrefix.trim().toLowerCase();
    if (!prefix || prefix.length < 2) return;
    setEmailSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/organizations/${orgId}/general-inbox-email`, { email_prefix: prefix }, { headers });
      setEditingEmailOrgId(null);
      // Trigger parent reload
      if (onCreateOrganization) {
        // Reuse the loadData callback by closing and reopening — or just reload
        window.location.reload();
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setEmailSaving(false);
    }
  };

  // Filter and sort organizations
  const processedOrganizations = useMemo(() => {
    let result = [...organizations];

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(org =>
        org.name?.toLowerCase().includes(searchLower) ||
        org.domain?.toLowerCase().includes(searchLower) ||
        org.description?.toLowerCase().includes(searchLower)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric fields
      if (sortField === 'user_count' || sortField === 'active_trips') {
        aVal = aVal || 0;
        bVal = bVal || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle string fields
      aVal = (aVal || '').toLowerCase();
      bVal = (bVal || '').toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [organizations, searchTerm, sortField, sortDirection]);

  // Paginate
  const totalPages = Math.ceil(processedOrganizations.length / itemsPerPage);
  const paginatedOrganizations = processedOrganizations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 text-ui-faint" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3 h-3 text-ui-link" />
      : <ArrowDown className="w-3 h-3 text-ui-link" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-ui-ink">Organizations</h2>
          <p className="text-ui-muted">Manage organizations and their settings</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search organizations..."
              className="pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-ui-primary w-64 bg-ui-sunk border-ui-control text-ui-ink placeholder-ui-faint"
            />
          </div>
          <button
            onClick={onCreateOrganization}
            className="bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary px-4 py-2 rounded-md font-medium"
          >
            + Create Organization
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="text-sm text-ui-muted">
        Showing {paginatedOrganizations.length} of {processedOrganizations.length} organizations
        {searchTerm && ` matching "${searchTerm}"`}
      </div>

      <div className="rounded-lg shadow-ui-card overflow-hidden bg-ui-surface">
        <table className="min-w-full divide-y divide-ui-divider">
          <thead className="bg-ui-sunk">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer text-ui-ink-2 hover:bg-ui-hover"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Organization
                  <SortIcon field="name" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer text-ui-ink-2 hover:bg-ui-hover"
                onClick={() => handleSort('domain')}
              >
                <div className="flex items-center gap-1">
                  Domain
                  <SortIcon field="domain" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer text-ui-ink-2 hover:bg-ui-hover"
                onClick={() => handleSort('user_count')}
              >
                <div className="flex items-center gap-1">
                  Users
                  <SortIcon field="user_count" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer text-ui-ink-2 hover:bg-ui-hover"
                onClick={() => handleSort('active_trips')}
              >
                <div className="flex items-center gap-1">
                  Trips
                  <SortIcon field="active_trips" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ui-ink-2">Status</th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider w-32 text-ui-ink-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y bg-ui-surface divide-ui-divider">
            {paginatedOrganizations.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-ui-muted">
                  {searchTerm ? 'No organizations match your search' : 'No organizations found'}
                </td>
              </tr>
            ) : (
              paginatedOrganizations.map((org) => (
                <tr key={org.id} className="hover:bg-ui-hover">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-ui-ink">{org.name}</div>
                      <div className="text-sm text-ui-muted">{org.description || 'No description'}</div>
                      <div className="mt-1">
                        {editingEmailOrgId === org.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={editEmailPrefix}
                              onChange={(e) => setEditEmailPrefix(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                              className="text-xs border border-ui-control bg-ui-sunk text-ui-ink rounded px-1.5 py-0.5 w-32"
                              placeholder="email-prefix"
                              onKeyDown={(e) => e.key === 'Enter' && handleSaveEmail(org.id)}
                              autoFocus
                            />
                            <span className="text-xs text-ui-faint">@in.veyage.io</span>
                            <button onClick={() => handleSaveEmail(org.id)} disabled={emailSaving} className="text-xs text-ui-green-fg hover:text-ui-green-solid font-medium">
                              {emailSaving ? '...' : 'Save'}
                            </button>
                            <button onClick={() => setEditingEmailOrgId(null)} className="text-xs text-ui-faint hover:text-ui-ink-3">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-ui-muted">
                              {org.general_inbox_email ? `Inbox: ${org.general_inbox_email}` : 'No general inbox'}
                            </span>
                            <button onClick={() => handleEditEmail(org)} className="text-xs text-ui-link hover:text-ui-blue-fg">[Edit]</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-ink-2">
                    {org.domain || 'No domain'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-ink-2">
                    {org.user_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-ink-2">
                    {org.active_trips}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      org.auto_approval ? 'bg-ui-green-bg text-ui-green-fg' : 'bg-ui-amber-bg text-ui-amber-fg'
                    }`}>
                      {org.auto_approval ? 'Auto-approve' : 'Manual approval'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-row space-x-2 justify-center">
                      <button
                        onClick={() => onComponentSettings(org)}
                        className="p-2 text-ui-violet-fg hover:bg-ui-violet-bg rounded-md transition-colors"
                        title="Component Settings"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onSSODomains(org)}
                        className="p-2 text-ui-green-fg hover:bg-ui-green-bg rounded-md transition-colors"
                        title="Google SSO Domains"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onManageAdmins(org)}
                        className="p-2 text-ui-blue-fg hover:bg-ui-blue-bg rounded-md transition-colors"
                        title="Manage Admins"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDeleteOrganization(org.id)}
                        className="p-2 text-ui-red-fg hover:bg-ui-red-bg rounded-md transition-colors"
                        title="Delete Organization"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-ui-muted">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="inline-flex items-center px-3 py-1.5 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed border-ui-control text-ui-ink-2 hover:bg-ui-hover"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1.5 rounded-md text-sm ${
                      currentPage === pageNum
                        ? 'bg-ui-primary text-ui-on-primary'
                        : 'border border-ui-control text-ui-ink-2 hover:bg-ui-hover'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="inline-flex items-center px-3 py-1.5 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed border-ui-control text-ui-ink-2 hover:bg-ui-hover"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Deleted Organizations Tab Component
const DeletedOrganizationsTab = ({ deletedOrganizations, onReinstateOrganization, onPermanentlyDelete, onRefresh }) => {
  const formatTimeRemaining = (reinstateBeforeDate) => {
    const now = new Date();
    const deadline = new Date(reinstateBeforeDate);
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return "Expired";
    if (diffDays === 1) return "1 day";
    return `${diffDays} days`;
  };

  const formatDeletedDate = (deletedAtDate) => {
    return new Date(deletedAtDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-ui-ink">Deleted Organizations</h2>
          <p className="text-ui-muted">Organizations pending deletion (30-day retention period)</p>
        </div>
        <button
          onClick={onRefresh}
          className="bg-ui-hover hover:bg-ui-line text-ui-ink-2 px-4 py-2 rounded-md font-medium"
        >
          Refresh
        </button>
      </div>

      <div className="rounded-lg shadow-ui-card overflow-hidden bg-ui-surface">
        {deletedOrganizations.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4 text-ui-muted">🗑️</div>
            <h3 className="text-lg font-medium mb-2 text-ui-ink">No deleted organizations</h3>
            <p className="text-ui-muted">Organizations deleted in the last 30 days will appear here</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-ui-divider">
            <thead className="bg-ui-sunk">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ui-ink-2">Organization</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ui-ink-2">Deleted At</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ui-ink-2">Deleted By</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ui-ink-2">Time Remaining</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider w-24 text-ui-ink-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y bg-ui-surface divide-ui-divider">
              {deletedOrganizations.map((org) => {
                const timeRemaining = formatTimeRemaining(org.reinstate_before);
                const isExpired = timeRemaining === "Expired";

                return (
                  <tr key={org.id} className={`hover:bg-ui-hover ${isExpired ? 'bg-ui-red-bg' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-ui-ink">{org.name}</div>
                        <div className="text-sm text-ui-muted">{org.description || 'No description'}</div>
                        {org.domain && (
                          <div className="text-xs text-ui-muted">Domain: {org.domain}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-ink-2">
                      {formatDeletedDate(org.deleted_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-ink-2">
                      {org.deleted_by_name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        isExpired
                          ? 'bg-ui-red-bg text-ui-red-fg'
                          : timeRemaining.includes('1 day') || timeRemaining.includes('2 day') || timeRemaining.includes('3 day')
                            ? 'bg-ui-amber-bg text-ui-amber-fg'
                            : 'bg-ui-green-bg text-ui-green-fg'
                      }`}>
                        {isExpired ? 'Expired' : `${timeRemaining} left`}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-row space-x-2 justify-center">
                        {!isExpired ? (
                          <button
                            onClick={() => onReinstateOrganization(org.id)}
                            className="p-2 text-ui-green-fg hover:bg-ui-green-bg rounded-md transition-colors"
                            title="Reinstate Organization"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        ) : (
                          <div className="p-2 text-ui-faint" title="Cannot reinstate (expired)">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                            </svg>
                          </div>
                        )}
                        <button
                          onClick={() => onPermanentlyDelete(org.id)}
                          className="p-2 text-ui-red-fg hover:bg-ui-red-bg rounded-md transition-colors"
                          title="Delete Forever"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {deletedOrganizations.length > 0 && (
        <div className="bg-ui-amber-bg border border-ui-amber-line rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-ui-amber-solid mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-ui-amber-fg">Retention Policy</h3>
              <div className="mt-1 text-sm text-ui-amber-fg">
                <p>Organizations are retained for 30 days after deletion. All users and data are preserved during this period and can be reinstated at any time before the deadline.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationManagement;
